import { Form, Formik, FormikProps } from "formik";
import React, { useContext, useState } from "react";
import {
  CompanyFormData,
  PredefinedDataTypes,
} from "../../../../../../utils/formTypes";
import { companyValidation } from "../../../../../../utils/validationSchema";
import { GetSubsidiaries } from "../queries/queries";
import {
  EMPTY_STRING,
  emptyMessage,
  ROWS_PER_PAGE,
} from "../../../../../../utils/constants";
import { OrderDirection } from "../../../../../../utils/commonQueryTypes";
import {
  AtsSubsidiaryOrderField,
  UpdateSubsidiaryResult,
  UpdateSubsidiaryVar,
} from "../queries/types";

import useUrlParams from "../../../../../../customHooks/useUrlParams";
import { useMutation } from "@apollo/client";
import { UpdateSubsidiary } from "../queries/mutations";
import { Operation } from "../../../../../../utils/Enum.types";
import useLoggedInUserDetails from "../../../../../../customHooks/useLoggedInUserDetails";
import {
  addCountryCode,
  removeMoreSpace,
} from "../../../../../../utils/UtilFunctions";
import { AppContext } from "../../../../../../context/context";
import { Button } from "../../../../../../stories/Button/Button";
import MessageModal from "../../../../../../customComponents/MessageModal";
import { AntSwitch } from "../../../../../../styles/Switch";
import { Label } from "../../../../../../stories/Label/Label";
import PredefinedAutocomplete from "../../../../../../customComponents/PredefinedAutocmplete";
import { LabelProps } from "../../../../../../utils/types";
import { FormControlLabel } from "@mui/material";
import InputHoc from "../../../../../common/Input/Index";
import TextArea from "../../../../../../stories/TextArea/TextArea";
import InputNumber from "../../../../../common/Input/InputNumber";
import LoadingModal from "../../../../../pages/LoadingModal";
import PredefinedSelect from "../../../../../../customComponents/PredefinedSelect";
import useLabels from "../../../../../../customHooks/useLabels";
import useFormJson from "../../../../../../json/useFormJson";
import IconSelect from "../../../../../../customComponents/IconSelect";

interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const CompanyDetails = ({ setModalFlag }: Props) => {

  const { state } = useContext(AppContext);
  const { companyFormLabels } = useFormJson();

  const { customer_id, company_id } = useUrlParams();
  const { user_details } = useLoggedInUserDetails();
  const [edit, setEdit] = useState(false);
  const [message, setMessage] = useState(emptyMessage);
  const { companyLabel } = useLabels();
  const initialValues: CompanyFormData = {
    // Company Details
    name: "", // Default: empty string for company name
    address: "", // Default: empty string for address
    pin: "", // Default: empty string for pin code
    telephone: "", // Default: empty string for telephone
    email: "", // Default: empty string for email
    mobile: "", // Default: empty string for mobile number
    place: "", // Default: empty string for place
    country: "", // Default: "India" for country
    state: "", // Default: empty string for state

    // Contact Details
    contactPerson: "", // Default: empty string for contact person name
    contactPersonDesign: "", // Default: empty string for contact person designation
    contactPersonEmail: "", // Default: empty string for contact person email
    contactPersonMobile: "", // Default: empty string for contact person mobile number
    url: "", // Default: empty string for company URL

    // Statuary Details
    enableGst: false, // Default: false for enable GST
    gstNo: "", // Default: empty string for GSTIN number
    tanNo: "", // Default: empty string for TAN registration number
    panNo: "", // Default: empty string for PAN registration number
    enableTds: false, // Default: false for enable TDS
    enableTcs: false, // Default: false for enable TCS
    vatNo: "",
    regType: "", // Default: empty string for registration type
    gstState: "", // Default: empty string for GST state
    latitude: "",
    longitude: "",
    gstStateCode: "",
    icon: "",
  };
  const [formData, setFormData] = useState<CompanyFormData>(initialValues);

  const [UpdateSubDetails, { loading: UpdateSubsidiaryLoading }] = useMutation<
    UpdateSubsidiaryResult,
    UpdateSubsidiaryVar
  >(UpdateSubsidiary, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  const handleSubmit = async () => {
    try {
    
      if (customer_id) {
        const customerUpdated = UpdateSubDetails({
          variables: {
          
            user_details,
            subsidiaryID: state.subsidiaryId,
            input: {
              name: removeMoreSpace(formData.address),
              address: removeMoreSpace(formData.address),
              place: removeMoreSpace(formData.place),
              email: formData.email,
              mobile: addCountryCode(formData.mobile),
              pin: formData.pin,
              state: formData.state,
              contactPerson: removeMoreSpace(formData.contactPerson),
              contactPersonDesign: formData.contactPersonDesign,
              contactPersonEmail: formData.contactPersonEmail,
              contactPersonMobile: addCountryCode(formData.contactPersonMobile),
              country: formData.country,
              gstNo: formData.gstNo,
              latitude: Number(formData.latitude),
              longitude: Number(formData.longitude),
              logoFilename: "",
              panNo: formData.panNo,
              tanNo: formData.tanNo,
              url: formData.url,
              vatNo: "",
              oth1No: "",
              oth2No: "",
              enableGst: formData.enableGst,
              enableTcs: formData.enableTcs,
              enableTds: formData.enableTds,
              icon: formData.icon,
            },
          },
          refetchQueries: [
            {
              query: GetSubsidiaries,
              variables: {
                after: null,
                customerID: customer_id,
                companyID: company_id,
                empID: user_details.id,
                first: ROWS_PER_PAGE,
                orderBy: {
                  direction: OrderDirection.ASC,
                  field: AtsSubsidiaryOrderField.NAME,
                },
                
                name: EMPTY_STRING,
              },
            },
          ],
        });
        const { data } = await customerUpdated;

        if (data && data.UpdateSubsidiary) {
          setMessage({
            flag: true,
            message: `${companyLabel} details updated successfully`,
            operation: Operation.CREATE,
          });
        }
      }
    } catch (err) {
      setMessage({
        flag: true,
        message: (err as Error).message,
        operation: Operation.NONE,
      });
    }
  };
  const hanldeClear = () => {
    setFormData(initialValues);
  };

  const handleClose = () => {
    setMessage(emptyMessage);
    if (message.operation !== Operation.NONE) {
      hanldeClear();
    }
  };

  const handleChecked = (
    e: React.ChangeEvent<HTMLInputElement>,
    meta: FormikProps<CompanyFormData>
  ) => {
    const { name, checked } = e.target as HTMLInputElement;

    setFormData(() => ({
      ...formData,
      [name]: checked,
    }));
    meta.handleChange(e);
  };

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<CompanyFormData>
  ) => {
    const { name, value } = e.target as HTMLInputElement;

    setFormData(() => ({
      ...formData,
      [name]: value,
    }));
    meta.handleChange(e);
  };

  return (
    <>
      <Formik
        initialValues={formData}
        validationSchema={companyValidation}
        onSubmit={handleSubmit}
        enableReinitialize
        validateOnChange
      >
        {(meta) => {
          return (
            <Form className="subsidary__view--form--company-details">
              <div className="subsidary__add--form-details">
                <div className="company-details__block--left">
                  <div className="company-details__block--details">
                    <h4>{companyLabel} Details</h4>
                  </div>
                  {companyFormLabels.companyDetails.map(
                    (field: LabelProps, key: React.Key) => {
                      if (field.dataType === "textarea") {
                        return (
                          <>
                            <div className="label-grid" key={key}>
                              <Label>{field.labelName}</Label>
                              <TextArea
                                value={formData[field.inputName]}
                                onChange={(
                                  e: React.ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                  handleValueChange(e, meta);
                                }}
                                type={field.dataType}
                                rows={3}
                                name={field.inputName}
                                disabled={!edit}
                              />
                            </div>
                            {meta.touched && meta.errors[field.inputName] ? (
                              <div className="label-grid">
                                <span></span>
                                <span className="nodata">
                                  {meta.errors[field.inputName]}
                                </span>
                              </div>
                            ) : null}
                          </>
                        );
                      } else if (field.dataType === "number") {
                        return (
                          <div className="label-grid" key={key}>
                            <Label>{field.labelName}</Label>
                            <InputNumber
                              values={formData[field.inputName]}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleValueChange(e, meta);
                              }}
                              name={field.inputName}
                              required={field.required}
                              disabled={!edit}
                            />
                            {meta.touched && meta.errors[field.inputName] ? (
                              <div className="label-grid">
                                <span></span>
                                <span className="nodata">
                                  {meta.errors[field.inputName]}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        );
                      } else if (field.dataType === "city") {
                        return (
                          <div className="label-grid">
                            <Label>{field.labelName}</Label>
                            <PredefinedSelect
                              label=""
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  place: e,
                                });
                              }}
                              type={PredefinedDataTypes.CITY}
                              value={formData.place as string}
                              name={field.inputName as string}
                            />
                          </div>
                        );
                      } else if (field.dataType === "state") {
                        return (
                          <div className="label-grid">
                            <Label>{field.labelName}</Label>
                            <PredefinedSelect
                              label=""
                              onChange={(e, state_code) => {
                                setFormData({
                                  ...formData,
                                  state: e,
                                  gstStateCode: state_code
                                    ? state_code
                                    : EMPTY_STRING,
                                });
                              }}
                              type={PredefinedDataTypes.STATE}
                              value={formData.state as string}
                              name={field.inputName as string}
                            />
                          </div>
                        );
                      } else if (field.dataType === "country") {
                        return (
                          <div className="label-grid">
                            <Label>{field.labelName}</Label>
                            <PredefinedSelect
                              label=""
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  country: e,
                                });
                              }}
                              type={PredefinedDataTypes.COUNTRY}
                              value={formData.country as string}
                              name={field.inputName as string}
                            />
                          </div>
                        );
                      } else {
                        return (
                          <React.Fragment key={key}>
                            <InputHoc
                              LabelName={field.labelName}
                              values={formData[field.inputName]}
                              name={field.inputName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleValueChange(e, meta);
                              }}
                              required={field.required}
                              autoFocus={field.autoFocus}
                              disabled={!edit}
                            />
                            {meta.touched && meta.errors[field.inputName] ? (
                              <div className="label-grid">
                                <span></span>
                                <span className="nodata">
                                  {meta.errors[field.inputName]}
                                </span>
                              </div>
                            ) : null}
                          </React.Fragment>
                        );
                      }
                    }
                  )}
                  <div className="company-details__block--details">
                    <h4>Contact Details</h4>
                  </div>
                  {companyFormLabels.contactDetails.map(
                    (field: LabelProps, key: React.Key) => {
                      if (field.dataType === "textarea") {
                        return (
                          <>
                            <div className="label-grid" key={key}>
                              <Label>{field.labelName}</Label>
                              <TextArea
                                value={formData[field.inputName]}
                                onChange={(
                                  e: React.ChangeEvent<HTMLTextAreaElement>
                                ) => {
                                  handleValueChange(e, meta);
                                }}
                                type={field.dataType}
                                rows={3}
                                name={field.inputName}
                                disabled={!edit}
                              />
                            </div>
                            {meta.touched && meta.errors[field.inputName] ? (
                              <div className="label-grid">
                                <span></span>
                                <span className="nodata">
                                  {meta.errors[field.inputName]}
                                </span>
                              </div>
                            ) : null}
                          </>
                        );
                      } else if (field.dataType === "number") {
                        return (
                          <div className="label-grid" key={key}>
                            <Label>{field.labelName}</Label>
                            <InputNumber
                              values={formData[field.inputName]}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleValueChange(e, meta);
                              }}
                              name={field.inputName}
                              required={field.required}
                              disabled={!edit}
                            />
                            {meta.touched && meta.errors[field.inputName] ? (
                              <div className="label-grid">
                                <span></span>
                                <span className="nodata">
                                  {meta.errors[field.inputName]}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        );
                      } else if (field.dataType === "city") {
                        return (
                          <div className="label-grid">
                            <Label>{field.labelName}</Label>
                            <PredefinedSelect
                              label=""
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  place: e,
                                });
                              }}
                              type={PredefinedDataTypes.CITY}
                              value={formData.place as string}
                              name={field.inputName as string}
                            />
                          </div>
                        );
                      } else if (field.dataType === "state") {
                        return (
                          <div className="label-grid">
                            <Label>{field.labelName}</Label>
                            <PredefinedSelect
                              label=""
                              onChange={(e, state_code) => {
                                setFormData({
                                  ...formData,
                                  state: e,
                                  gstStateCode: state_code
                                    ? state_code
                                    : EMPTY_STRING,
                                });
                              }}
                              type={PredefinedDataTypes.STATE}
                              value={formData.state as string}
                              name={field.inputName as string}
                            />
                          </div>
                        );
                      } else if (field.dataType === "country") {
                        return (
                          <div className="label-grid">
                            <Label>{field.labelName}</Label>
                            <PredefinedSelect
                              label=""
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  country: e,
                                });
                              }}
                              type={PredefinedDataTypes.COUNTRY}
                              value={formData.country as string}
                              name={field.inputName as string}
                            />
                          </div>
                        );
                      } else {
                        return (
                          <React.Fragment key={key}>
                            <InputHoc
                              LabelName={field.labelName}
                              values={formData[field.inputName]}
                              name={field.inputName}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                handleValueChange(e, meta);
                              }}
                              required={field.required}
                              disabled={!edit}
                            />
                            {meta.touched && meta.errors[field.inputName] ? (
                              <div className="label-grid">
                                <span></span>
                                <span className="nodata">
                                  {meta.errors[field.inputName]}
                                </span>
                              </div>
                            ) : null}
                          </React.Fragment>
                        );
                      }
                    }
                  )}
                </div>
                <div className="company-details__block--right">
                  <div className="company-details__block--details">
                    <h4>Satutory Details</h4>
                  </div>
                  {companyFormLabels.statuaryDetails.map(
                    (field: LabelProps, key: React.Key) => {
                      if (field.inputName === "enableGst") {
                        return (
                          <div
                            className={`company-details__block--right--gst${
                              !formData.enableGst ? "" : "--wb"
                            }`}
                          >
                            <FormControlLabel
                              label="Enable GST"
                              labelPlacement="start"
                              name={field.inputName}
                              control={
                                <AntSwitch
                                  checked={formData[field.inputName]}
                                  // onClick={() => {
                                  //   setGst(!gst);
                                  // }}
                                  onChange={(e) => {
                                    handleChecked(e, meta);
                                  }}
                                />
                              }
                              className="company-details__block--right--gst--form-labels"
                            />
                            {formData.enableGst && (
                              <>
                                <div className="company-details__block--subdetails">
                                  <h4>GST Details</h4>
                                </div>
                                {field.formFields
                                  ? field.formFields.map((sub_field) => {
                                      if (sub_field.dataType === "state") {
                                        return (
                                          <div className="label-grid">
                                            <Label>{sub_field.labelName}</Label>
                                            <PredefinedSelect
                                              label=""
                                              onChange={(e, state_code) => {
                                                setFormData({
                                                  ...formData,
                                                  gstState: e,
                                                  gstStateCode: state_code
                                                    ? state_code
                                                    : EMPTY_STRING,
                                                });
                                              }}
                                              type={PredefinedDataTypes.STATE}
                                              value={
                                                formData.gstState as string
                                              }
                                              name={field.inputName as string}
                                            />
                                          </div>
                                        );
                                      } else if (
                                        sub_field.dataType === "regType"
                                      ) {
                                        return (
                                          <div className="label-grid">
                                            <Label>{sub_field.labelName}</Label>
                                            <PredefinedAutocomplete
                                              label=""
                                              onChange={(newValue) => {
                                                if (newValue) {
                                                  setFormData({
                                                    ...formData,
                                                    [sub_field.inputName]:
                                                      newValue.value,
                                                  });
                                                } else {
                                                  setFormData({
                                                    ...formData,
                                                    [sub_field.inputName]: "",
                                                  });
                                                }
                                              }}
                                              type={
                                                PredefinedDataTypes.NATIONALITY
                                              }
                                              value={
                                                formData[
                                                  sub_field.inputName
                                                ] as string
                                              }
                                              disabled={!edit}
                                            />
                                          </div>
                                        );
                                      } else {
                                        return (
                                          <React.Fragment key={key}>
                                            <InputHoc
                                              LabelName={sub_field.labelName}
                                              values={
                                                formData[sub_field.inputName]
                                              }
                                              name={sub_field.inputName}
                                              onChange={(
                                                e: React.ChangeEvent<HTMLInputElement>
                                              ) => {
                                                handleValueChange(e, meta);
                                              }}
                                              disabled={!edit}
                                            />
                                            {meta.touched &&
                                            meta.errors[field.inputName] ? (
                                              <div className="label-grid">
                                                <span></span>
                                                <span className="nodata">
                                                  {meta.errors[field.inputName]}
                                                </span>
                                              </div>
                                            ) : null}
                                          </React.Fragment>
                                        );
                                      }
                                    })
                                  : null}
                              </>
                            )}
                          </div>
                        );
                      } else if (field.inputName === "icon") {
                        return (
                          <IconSelect
                            onChange={(newValue) => {
                              if (newValue) {
                                setFormData({
                                  ...formData,
                                  icon: newValue.value as unknown as string,
                                });
                              } else {
                                setFormData({
                                  ...formData,
                                  icon: "",
                                });
                              }
                            }}
                          />
                        );
                      } else {
                        return (
                          <FormControlLabel
                            label={field.labelName}
                            labelPlacement="start"
                            name={field.inputName}
                            key={key}
                            control={
                              <AntSwitch
                                checked={formData[field.inputName] as boolean}
                                onChange={(e) => {
                                  handleChecked(e, meta);
                                }}
                              />
                            }
                            className="company-details__block--right--gst--form-labels"
                          />
                        );
                      }
                    }
                  )}
                </div>
              </div>
              {!edit ? (
                <Button
                  mode="edit"
                  type="button"
                  onClick={() => setEdit(true)}
                />
              ) : (
                <Button mode="save" type="submit" />
              )}
              <Button mode="clear" type="button" />
              <Button mode="cancel" onClick={() => setModalFlag(false)} />

              <MessageModal
                handleClose={handleClose}
                modalFlag={message.flag}
                operation={message.operation}
                value={message.message}
              />
            </Form>
          );
        }}
      </Formik>
      <LoadingModal flag={UpdateSubsidiaryLoading} />
    </>
  );
};

export default CompanyDetails;
